import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useMemo, useRef, useState } from 'react';
import { ArrowLeft, Upload, Link2, Check, ImageOff, Package } from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import { hardwareApi, type DeviceModelRow } from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';

/**
 * BULK PHOTO BACKFILL.
 *
 * 24 of 31 device models carry no hero image, and the only way to add one was
 * to open each device's editor in turn. Receiving is the screen that suffers:
 * with no photo, an operator identifies a P17 from a QD4 by reading a model
 * name, which is exactly what the search redesign was meant to stop.
 *
 * Deliberately NOT a new gallery. Uploads go through the same
 * `hardwareApi.presignPhoto` → S3 PUT that `ImageGallery` uses, with the same
 * caps, so there is one upload path to maintain. This screen only removes the
 * navigation between 24 of them.
 *
 * A pasted URL is saved verbatim and never copied into our bucket — an existing
 * model already points at a vendor CDN, so this is a shape the data already
 * takes. It costs nothing to host and breaks if the vendor moves the file;
 * that trade is the operator's to make, per row.
 */

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

type TileState = { busy?: boolean; error?: string; savedUrl?: string };

const DeviceImageBackfill: React.FC<{
  models: DeviceModelRow[];
  onClose: () => void;
  /** Called after any successful save so the parent can refetch its models. */
  onSaved: () => void;
}> = ({ models, onClose, onSaved }) => {
  const [state, setState] = useState<Record<string, TileState>>({});
  const [urlDraft, setUrlDraft] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  // Nameless rows are excluded for the same reason the search hides them: a
  // blank tile is not identifiable, so a photo could be attached to the wrong
  // device with no way to tell.
  const named = useMemo(
    () => models.filter((m) => (m.modelName || '').trim().length > 0)
      .sort((a, b) => a.modelName.localeCompare(b.modelName)),
    [models],
  );
  const heroOf = (m: DeviceModelRow) => state[m.modelId]?.savedUrl || m.media?.heroImageUrl;
  /**
   * EVERY device, always — never filtered to the ones missing a picture.
   * A list that hides what already has a photo cannot be used to REPLACE one,
   * and it makes the count on screen disagree with the count on the button the
   * moment an upload lands.
   */
  const shown = named;
  const withPhoto = named.filter((m) => heroOf(m)).length;

  /** Writes the hero back onto the model, preserving every other field. */
  const saveHero = async (m: DeviceModelRow, url: string) => {
    // upsertModel replaces the row, so the whole model is sent back with only
    // `media` changed. Sending `{ modelId, media }` alone would blank the rest.
    await hardwareApi.upsertModel({
      ...m,
      media: {
        ...(m.media || { photos: [] }),
        heroImageUrl: url,
        photos: Array.from(new Set([...(m.media?.photos || []), url])),
      },
    });
  };

  const uploadFile = async (m: DeviceModelRow, file: File) => {
    if (!ACCEPTED.includes(file.type)) {
      setState((s) => ({ ...s, [m.modelId]: { error: 'PNG, JPEG, WebP or GIF only' } }));
      return;
    }
    if (file.size > MAX_BYTES) {
      setState((s) => ({ ...s, [m.modelId]: { error: `Too large — ${(file.size / 1048576).toFixed(1)}MB of 5MB` } }));
      return;
    }
    setState((s) => ({ ...s, [m.modelId]: { busy: true } }));
    try {
      const { uploadUrl, fileUrl } = await hardwareApi.presignPhoto({
        modelId: m.modelId, filename: file.name, contentType: file.type, bytes: file.size,
      });
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      await saveHero(m, fileUrl);
      setState((s) => ({ ...s, [m.modelId]: { savedUrl: fileUrl } }));
      onSaved();
    } catch (e: any) {
      setState((s) => ({ ...s, [m.modelId]: { error: e?.message || 'Upload failed' } }));
    }
  };

  const saveUrl = async (m: DeviceModelRow) => {
    const url = (urlDraft[m.modelId] || '').trim();
    if (!url) return;
    if (!/^https:\/\/\S+$/i.test(url)) {
      setState((s) => ({ ...s, [m.modelId]: { error: 'Needs a full https:// URL' } }));
      return;
    }
    setState((s) => ({ ...s, [m.modelId]: { busy: true } }));
    try {
      await saveHero(m, url);
      setState((s) => ({ ...s, [m.modelId]: { savedUrl: url } }));
      setUrlDraft((d) => ({ ...d, [m.modelId]: '' }));
      onSaved();
    } catch (e: any) {
      setState((s) => ({ ...s, [m.modelId]: { error: e?.message || 'Could not save' } }));
    }
  };

  return (
    <div>
      <div>
        <button
          onClick={onClose}
          className="mb-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-semibold text-stone-500 hover:bg-stone-100 hover:text-stone-800"
        >
          <ArrowLeft size={14} /> Receiving
        </button>
        <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-stone-900 pb-2.5">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-400">Receiving · Photos</div>
            <h2 className="text-xl font-bold tracking-tight text-stone-900">Device photos</h2>
            <p className="mt-0.5 text-[12px] text-stone-500">
              Drop an image on a tile, or paste a vendor image URL. Saves immediately.
            </p>
          </div>
        </div>
        <div className="h-[3px] w-32 bg-gradient-to-r from-[#DC143C] to-[#9F1239]" />

        <div className="mt-4 border-b border-stone-200 pb-3">
          <span className="text-[13px] text-stone-600">
            {named.length} devices · <b className="text-stone-900">{withPhoto}</b> with a photo
          </span>
        </div>

        <div className="mt-4">
          {shown.length === 0 ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
              <ImageOff size={26} className="mx-auto mb-2 text-stone-300" />
              <p className="text-sm font-medium text-stone-700">No devices to show.</p>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-3">
              {shown.map((m) => {
                const st = state[m.modelId] || {};
                const hero = heroOf(m);
                return (
                  <div key={m.modelId} className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragOver(m.modelId); }}
                      onDragLeave={() => setDragOver(null)}
                      onDrop={(e) => {
                        e.preventDefault(); setDragOver(null);
                        const f = e.dataTransfer.files?.[0];
                        if (f) uploadFile(m, f);
                      }}
                      onClick={() => fileInputs.current[m.modelId]?.click()}
                      className={`flex h-[132px] cursor-pointer items-center justify-center border-b border-stone-100 ${
                        dragOver === m.modelId ? 'bg-[#FFF1F3] ring-2 ring-inset ring-[#DC143C]' : 'bg-stone-50'
                      }`}
                    >
                      {st.busy ? (
                        <RubyGemMark size={22} drawing />
                      ) : hero ? (
                        <ShimmerImage
                          src={hero} alt={m.modelName} width="100%" height={130}
                          objectFit="contain" variant="light"
                          fallbackIcon={<ImageOff size={20} className="text-stone-300" />}
                          fallbackBgColor="bg-stone-50"
                        />
                      ) : (
                        <span className="flex flex-col items-center gap-1.5 text-stone-400">
                          <Upload size={20} />
                          <span className="text-[11px] font-medium">Drop or click</span>
                        </span>
                      )}
                      <input
                        ref={(el) => { fileInputs.current[m.modelId] = el; }}
                        type="file" accept={ACCEPTED.join(',')} hidden
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(m, f); e.target.value = ''; }}
                      />
                    </div>

                    <div className="p-2.5">
                      <p className="truncate text-[12.5px] font-semibold text-stone-900" title={m.modelName}>{m.modelName}</p>
                      <p className="mt-0.5 flex items-center gap-1 truncate text-[10.5px] text-stone-500">
                        <Package size={10} className="shrink-0" />{m.manufacturer}
                      </p>

                      {st.savedUrl && !st.busy && (
                        <p className="mt-1.5 flex items-center gap-1 text-[10.5px] font-semibold text-emerald-700">
                          <Check size={11} /> Saved
                        </p>
                      )}
                      {st.error && <p className="mt-1.5 text-[10.5px] font-medium text-[#DC143C]">{st.error}</p>}

                      <div className="mt-2 flex items-center gap-1">
                        <Link2 size={12} className="shrink-0 text-stone-400" />
                        <input
                          value={urlDraft[m.modelId] || ''}
                          onChange={(e) => setUrlDraft((d) => ({ ...d, [m.modelId]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveUrl(m); }}
                          placeholder="Paste image URL"
                          aria-label={`Image URL for ${m.modelName}`}
                          className="min-w-0 flex-1 rounded-md border border-stone-200 px-1.5 py-1 text-[10.5px] outline-none focus:border-[#DC143C]"
                        />
                        <button
                          onClick={() => saveUrl(m)}
                          disabled={!((urlDraft[m.modelId] || '').trim()) || st.busy}
                          className="rounded-md px-2 py-1 text-[10.5px] font-bold text-white disabled:opacity-30"
                          style={{ background: '#DC143C' }}
                        >Set</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end border-t border-stone-200 pt-4">
          <button
            onClick={() => { toastService.success('Photos updated.'); onClose(); }}
            className="min-h-[44px] rounded-lg px-5 text-sm font-semibold text-white"
            style={{ background: '#DC143C' }}
          >Done</button>
        </div>
      </div>
    </div>
  );
};

export default DeviceImageBackfill;
